import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, CheckCircle2, MessageSquare, Wrench, XCircle } from 'lucide-react';
import { Badge } from '../components/Badge';
import { useChatRunDetail } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const STATUS_VARIANT: Record<string, 'success' | 'danger' | 'default'> = {
  completed: 'success',
  failed: 'danger',
};

const ROLE_LABEL: Record<string, string> = {
  user: 'User',
  assistant: 'Assistant',
  system: 'System',
  tool: 'Tool',
};

function formatDuration(ms: number | null) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

interface ChatRunDetailProps {
  runId: string;
}

export function ChatRunDetail({ runId }: ChatRunDetailProps) {
  const { data, isLoading } = useChatRunDetail(runId);

  if (isLoading || !data) {
    return <div className="text-text-muted p-5">Loading...</div>;
  }

  const { run, toolCalls, messages } = data;
  const variant = STATUS_VARIANT[run.status] ?? 'default';

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/chat-dashboard')}
        className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-primary transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Chat Orchestrator
      </button>

      <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-text">{run.id}</h1>
          <Badge variant={variant}>{run.status}</Badge>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-text-muted text-xs">Organization</dt>
            <dd className="text-text">{run.organizationId}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Duration</dt>
            <dd className="text-text">{formatDuration(run.durationMs)}</dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Tokens</dt>
            <dd className="text-text">
              {run.inputTokens.toLocaleString()} / {run.outputTokens.toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-text-muted text-xs">Cost</dt>
            <dd className="text-text">${parseFloat(run.costUsd).toFixed(4)}</dd>
          </div>
        </dl>
        {run.errorMessage && (
          <p className="text-sm text-danger">{run.errorMessage}</p>
        )}
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <MessageSquare size={16} className="text-primary" />
          <h2 className="text-sm font-medium text-text-secondary">Conversation Thread</h2>
          <span className="text-xs text-text-muted">({(messages ?? []).length} messages)</span>
        </div>
        <ul className="divide-y divide-border">
          {(messages ?? []).length > 0 ? (
            (messages ?? []).map((message) => (
              <li key={message.id} className="px-5 py-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {ROLE_LABEL[message.role] ?? message.role}
                  </span>
                  {message.agentType && (
                    <Badge variant="default">{message.agentType}</Badge>
                  )}
                  <span className="text-xs text-text-muted">
                    {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm text-text whitespace-pre-wrap break-words">
                  {message.content}
                </p>
              </li>
            ))
          ) : (
            <li className="px-5 py-8 text-center text-text-muted text-sm">
              No conversation messages
            </li>
          )}
        </ul>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Wrench size={16} className="text-primary" />
          <h2 className="text-sm font-medium text-text-secondary">Tool-Call Timeline</h2>
          <span className="text-xs text-text-muted">({toolCalls.length} calls)</span>
        </div>
        <ul className="divide-y divide-border">
          {toolCalls.length > 0 ? (
            toolCalls.map((call) => {
              const toolName = call.metadata?.toolName ?? 'unknown';
              const StatusIcon = call.status === 'completed' ? CheckCircle2 : XCircle;
              const callVariant = STATUS_VARIANT[call.status] ?? 'default';
              return (
                <li key={call.id} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusIcon size={14} />
                    <span className="text-sm text-text font-medium truncate">{toolName}</span>
                    <Badge variant={callVariant}>{call.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-text-muted whitespace-nowrap">
                    {call.errorMessage && (
                      <span className="text-danger">{call.errorMessage}</span>
                    )}
                    <span>{formatDuration(call.durationMs)}</span>
                    {call.startedAt && (
                      <span>
                        {formatDistanceToNow(new Date(call.startedAt), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </li>
              );
            })
          ) : (
            <li className="px-5 py-8 text-center text-text-muted text-sm">No tool calls</li>
          )}
        </ul>
      </div>
    </div>
  );
}
